import { useEffect, useRef, useState } from 'react'

import { OptionType, SelectProps, useForm } from '@verbit-ai/verbit-ui-library'
import { useInterval, useTheme } from '@chakra-ui/react'

import { Button } from 'src/components/VerbitWrappers/Button'
import { DEFAULT_FONT_FAMILY_BY_LANGUAGE, ThemeType } from 'src/themes/types'
import { FONT_FAMILIES_PRESENTATION } from 'src/components/ThemeToggle/types'
import { Modal } from 'src/components/VerbitWrappers/Modal'
import { paletteChromakey } from 'src/themes/palette.chromakey'
import { FONT_SIZE_BASE, FONT_SIZE_PERCENTAGE_OPTIONS } from 'src/models/UIConfiguration'
import { getTextFromReactNode } from 'src/utils/text'
import {
    APP_BACKGROUND_COLOR_INDEX_QUERY_PARAM,
    FONT_FAMILY_QUERY_PARAM,
    FONT_SIZE_PERCENTAGE_QUERY_PARAM,
    IS_FULL_WIDTH_MODE_QUERY_PARAM,
    REQUESTED_LANGUAGE_PARAM,
    SHOULD_DISPLAY_LOGO_QUERY_PARAM,
    SHOULD_SHOW_TRANSCRIPT_IN_ALL_CAPS_QUERY_PARAM,
    TEXT_BACKGROUND_COLOR_INDEX_QUERY_PARAM,
    TEXT_COLOR_INDEX_QUERY_PARAM,
    THEME_TYPE_QUERY_PARAM,
} from 'src/utils/env'
import { PARAGRAPH_LINE_HEIGHT_PROPORTION, PARAGRAPH_VERTICAL_PADDING } from 'src/components/constants'
import { Form } from 'src/components/VerbitWrappers/Form'
import { useLanguage } from 'src/contexts/LanguageContext'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'

import * as S from './styles'

const linesToDisplayOptions: OptionType[] = [2, 3].map((lines) => ({
    label: `${lines} lines`,
    value: lines.toString(),
}))
const backgroundColorOptions: OptionType[] = paletteChromakey.transcript.appBackgroundColors.map(({ colorTitle, colorValue }, index) => ({
    label: colorTitle,
    icon: <S.ColorIndicator $indicatorColor={colorValue} />,
    value: index.toString(),
}))
const textColorOptions: OptionType[] = paletteChromakey.transcript.textColors
    .filter(({ colorValue }) => !!colorValue)
    .map(({ colorTitle, colorValue }, index) => ({
        label: colorTitle,
        icon: <S.ColorIndicator $indicatorColor={colorValue} $withShadow={colorValue === '#ffffff'} />,
        value: index.toString(),
    }))
const fontSizeOptions: OptionType[] = FONT_SIZE_PERCENTAGE_OPTIONS.map((percentage) => ({
    label: `${percentage}%`,
    value: percentage.toString(),
}))

// this function allows us to compare two color options, to prevent using the same color
// for text and background (e.g. black text on black background);
// we rely on labels rather than values for this, because values are not guaranteed
// to be exactly the same (e.g. black text is #000000, and black background is #020219,
// yet they are surely both black)
const areColorOptionsTheSame = (optionA: OptionType, optionB: OptionType) => {
    const labelA = getTextFromReactNode(optionA.label)
    const labelB = getTextFromReactNode(optionB.label)

    return labelA === labelB
}

type LargeScreenDisplaySettingsProps = {
    isOpen: boolean
    onClose: () => void
}

const FORM_FIELD_LINES_TO_DISPLAY = 'linesToDisplay'
const FORM_FIELD_BACKGROUND_COLOR = 'backgroundColor'
const FORM_FIELD_TEXT_COLOR = 'textColor'
const FORM_FIELD_FONT_FAMILY = 'fontFamily'
const FORM_FIELD_FONT_SIZE = 'fontSize'
const FORM_FIELD_LANGUAGE = 'language'
const FORM_FIELD_SHOULD_DISPLAY_LOGO = 'shouldDisplayLogo'
const FORM_FIELD_SHOULD_DISPLAY_TRANSCRIPT_IN_ALL_CAPS = 'shouldDisplayTranscriptInAllCaps'
type SettingsFormValue = {
    [FORM_FIELD_LINES_TO_DISPLAY]: OptionType
    [FORM_FIELD_BACKGROUND_COLOR]: OptionType
    [FORM_FIELD_TEXT_COLOR]: OptionType
    [FORM_FIELD_FONT_FAMILY]: OptionType
    [FORM_FIELD_FONT_SIZE]: OptionType
    [FORM_FIELD_LANGUAGE]: OptionType
    [FORM_FIELD_SHOULD_DISPLAY_LOGO]: boolean
    [FORM_FIELD_SHOULD_DISPLAY_TRANSCRIPT_IN_ALL_CAPS]: boolean
}

export const LargeScreenDisplaySettings = (props: LargeScreenDisplaySettingsProps) => {
    const { isOpen, onClose } = props
    const { availableFontFamilies } = useTheme()
    const { translations, requestedLanguageName, isLanguageSelectionAvailable } = useLanguage()
    const isAllCapsDisplayAllowed = useFeatureFlag('allow_all_caps_display')

    const defaultFontFamily = DEFAULT_FONT_FAMILY_BY_LANGUAGE[requestedLanguageName]
    const fontFamilyOptions: OptionType[] = availableFontFamilies.map((fontFamily) => ({
        value: fontFamily,
        label: `${FONT_FAMILIES_PRESENTATION[fontFamily]}${fontFamily === defaultFontFamily ? ' (default)' : ''}`,
    }))

    const languageOptions: OptionType[] = translations.map(({ code, name }) => ({
        label: name,
        value: code,
    }))

    const formMethods = useForm<SettingsFormValue>({
        defaultValues: {
            linesToDisplay: linesToDisplayOptions[0],
            backgroundColor: backgroundColorOptions[0],
            textColor: textColorOptions[0],
            fontFamily: fontFamilyOptions.find((option) => option.value === defaultFontFamily)!,
            fontSize: fontSizeOptions[0],
            language: languageOptions[0],
            shouldDisplayLogo: true,
            shouldDisplayTranscriptInAllCaps: false,
        },
    })

    const { textColor, backgroundColor } = formMethods.getValues()
    useEffect(() => {
        if (areColorOptionsTheSame(textColor, backgroundColor)) {
            formMethods.setValue('textColor', textColorOptions.find((option) => !areColorOptionsTheSame(option, backgroundColor))!)
        }
    }, [backgroundColor, formMethods, textColor])

    const displayWindowRef = useRef<Window | null>(null)
    const [isDisplayWindowActive, setIsDisplayWindowActive] = useState(false)

    useInterval(
        () => {
            if (displayWindowRef.current?.closed) {
                setIsDisplayWindowActive(false)
                displayWindowRef.current = null
            }
        },
        isOpen ? 1000 : null,
    )

    const openDisplay = () => {
        const values = formMethods.getValues()
        const themeType: ThemeType = 'chromakey'

        const newUrl = new URL(window.location.href)
        newUrl.searchParams.set(THEME_TYPE_QUERY_PARAM, themeType)

        newUrl.searchParams.set(APP_BACKGROUND_COLOR_INDEX_QUERY_PARAM, values.backgroundColor.value)
        newUrl.searchParams.set(TEXT_BACKGROUND_COLOR_INDEX_QUERY_PARAM, values.backgroundColor.value)
        newUrl.searchParams.set(TEXT_COLOR_INDEX_QUERY_PARAM, values.textColor.value)
        newUrl.searchParams.set(FONT_FAMILY_QUERY_PARAM, values.fontFamily.value)
        newUrl.searchParams.set(FONT_SIZE_PERCENTAGE_QUERY_PARAM, values.fontSize.value)
        newUrl.searchParams.set(REQUESTED_LANGUAGE_PARAM, values.language.value)
        newUrl.searchParams.set(SHOULD_DISPLAY_LOGO_QUERY_PARAM, values.shouldDisplayLogo.toString())
        newUrl.searchParams.set(IS_FULL_WIDTH_MODE_QUERY_PARAM, 'true')
        newUrl.searchParams.set(SHOULD_SHOW_TRANSCRIPT_IN_ALL_CAPS_QUERY_PARAM, values.shouldDisplayTranscriptInAllCaps.toString())

        const windowWidth = window.screen.availWidth - 100
        const windowHeight =
            Number(values.linesToDisplay.value) *
                (Math.round((FONT_SIZE_BASE * Number(values.fontSize.value)) / 100) * PARAGRAPH_LINE_HEIGHT_PROPORTION) +
            PARAGRAPH_VERTICAL_PADDING * 2

        const features = [
            'popup=true',
            `width=${windowWidth}`,
            `height=${windowHeight}`,
            `left=${Math.round((window.outerWidth - windowWidth) / 2)}`,
            `top=${Math.round((window.outerHeight - windowHeight) / 2)}`,
        ].join(',')

        const resizeWindow = () => {
            displayWindowRef.current!.resizeTo(
                windowWidth + displayWindowRef.current!.outerWidth - displayWindowRef.current!.innerWidth,
                windowHeight + displayWindowRef.current!.outerHeight - displayWindowRef.current!.innerHeight,
            )
        }

        if (displayWindowRef.current) {
            displayWindowRef.current.location.assign(newUrl)
            resizeWindow()
        } else {
            displayWindowRef.current = window.open(newUrl, '_blank', features)
            // browsers may have inherent minimum height for windows opened programmatically -
            // for instance, for Chrome its 100px, so if user requests view for 2 lines at 100% font size,
            // we would _request_ opening popup 64px in height, but it will be opened at 100px still;
            // as a workaround for this, we resize the window after it was opened
            displayWindowRef.current!.addEventListener('load', resizeWindow)
            setIsDisplayWindowActive(true)
        }

        displayWindowRef.current!.focus()
    }

    const onModalClose = () => {
        displayWindowRef.current?.close()
        setIsDisplayWindowActive(false)
        onClose()
    }

    const { palette } = useTheme()

    return (
        <Modal
            isOpen={isOpen}
            onClose={onModalClose}
            isCentered
            closeOnOverlayClick={false}
            contentStyle={{
                maxWidth: '470px',
                backgroundColor: palette.verbitModal.backgroundColor,
            }}>
            <Modal.Header>Large screen display</Modal.Header>
            <Modal.CloseButton />
            <Modal.Subtitle>Customize the display to fit your needs</Modal.Subtitle>
            <Modal.Body>
                <Form methods={formMethods} onSubmit={formMethods.handleSubmit(openDisplay)}>
                    <S.Row>
                        <S.Column>
                            <S.Label>Captions lines to display</S.Label>
                            <Form.Select name={FORM_FIELD_LINES_TO_DISPLAY} options={linesToDisplayOptions}></Form.Select>
                        </S.Column>
                    </S.Row>
                    <S.Row>
                        <S.Column>
                            <S.Label>Background color</S.Label>
                            <Form.Select
                                name={FORM_FIELD_BACKGROUND_COLOR}
                                options={backgroundColorOptions}
                                components={{ Option: S.StyledOption } as SelectProps['components']}></Form.Select>
                        </S.Column>
                        <S.Column>
                            <S.Label>Text color</S.Label>
                            <Form.Select
                                name={FORM_FIELD_TEXT_COLOR}
                                options={textColorOptions}
                                isOptionDisabled={(option) => areColorOptionsTheSame(option, backgroundColor)}
                                components={{ Option: S.StyledOption } as SelectProps['components']}></Form.Select>
                        </S.Column>
                    </S.Row>
                    <S.Row>
                        <S.Column>
                            <S.Label>Font family</S.Label>
                            <Form.Select name={FORM_FIELD_FONT_FAMILY} options={fontFamilyOptions}></Form.Select>
                        </S.Column>
                    </S.Row>
                    <S.Row>
                        <S.Column>
                            <S.Label>Font size</S.Label>
                            <Form.Select name={FORM_FIELD_FONT_SIZE} options={fontSizeOptions}></Form.Select>
                        </S.Column>
                    </S.Row>
                    {isLanguageSelectionAvailable && (
                        <S.Row>
                            <S.Column>
                                <S.Label>Display language</S.Label>
                                <Form.Select name={FORM_FIELD_LANGUAGE} options={languageOptions}></Form.Select>
                            </S.Column>
                        </S.Row>
                    )}
                    {isAllCapsDisplayAllowed && (
                        <Form.CheckboxGroup name={FORM_FIELD_SHOULD_DISPLAY_TRANSCRIPT_IN_ALL_CAPS}>
                            <Form.Checkbox>Display transcript in ALL CAPS</Form.Checkbox>
                        </Form.CheckboxGroup>
                    )}
                    <Form.CheckboxGroup name={FORM_FIELD_SHOULD_DISPLAY_LOGO}>
                        <Form.Checkbox>Display logo</Form.Checkbox>
                    </Form.CheckboxGroup>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={formMethods.handleSubmit(openDisplay)}>
                    {isDisplayWindowActive ? 'Save changes' : 'Open display'}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
